// DEPRECATED (2026-07-19): pwa/src/screens/ is scheduled for deletion in STEP-M8-V4-LEGACY-SCREENS.
// Active screens are in pwa/src/v4/screens/ -- use those instead.
// DiagnosticsScreen -- DR run results: verdict, findings, probe coverage, 7-day trend
import { useState, useEffect, useCallback } from "react";
import { fetchDiagnostics, fetchDigitalThreadHealth } from "../api/client.js";
import type { DiagnosticsResponse, DrFinding, DigitalThreadHealthResponse } from "../api/types.js";

const VERDICT_COLOR: Record<string, string> = {
  GREEN: "var(--ok)",
  YELLOW: "var(--warn)",
  RED: "var(--danger)",
};

const VERDICT_BG: Record<string, string> = {
  GREEN: "var(--ok-soft)",
  YELLOW: "var(--warn-soft)",
  RED: "var(--danger-soft)",
};

function verdictChipClass(verdict: string): string {
  if (verdict === "GREEN") return "ok";
  if (verdict === "YELLOW") return "warn";
  return "danger";
}

function annotationSeverityColor(pct: number): string {
  if (pct >= 85) return "var(--ok)";
  if (pct >= 70) return "var(--warn)";
  return "var(--danger)";
}

function orphanSeverityColor(count: number): string {
  if (count === 0) return "var(--ok)";
  if (count <= 9) return "var(--warn)";
  return "var(--danger)";
}

function timeAgo(isoString: string): string {
  const then = new Date(isoString).getTime();
  const elapsed = Date.now() - then;
  const h = Math.floor(elapsed / 3_600_000);
  if (h < 2) return "just now";
  if (h < 24) return h + "h ago";
  return Math.floor(h / 24) + "d ago";
}

function severityChipClass(sev: string): string {
  if (sev === "P0" || sev === "P1") return "danger";
  if (sev === "P2") return "warn";
  return "";
}

export interface DeduplicatedFinding {
  severity: string;
  title: string;
  domains: string[];
  recurrence: number;
  statuses: string[];
}

/** Group findings by severity+domain (probe class) signature. Sort most-recurring first.
 * NOTE: `title` is set from the first occurrence per group; subsequent titles within the
 * same probe class are not retained. Expand the finding row to see all affected domains. */
export function deduplicateFindings(findings: DrFinding[]): DeduplicatedFinding[] {
  const map = new Map<string, DeduplicatedFinding>();
  for (const f of findings) {
    // Derive a finding class from the title (text before the first ":" or "/").
    // This collapses instance-specific findings (e.g. 50 "Secret: X in file Y"
    // P0s from the same probe) into one grouped row with recurrence count.
    const colonIdx = f.title.indexOf(":");
    const slashIdx = f.title.indexOf("/");
    const sepIdx = [colonIdx, slashIdx].filter((i) => i > 0).reduce((a, b) => Math.min(a, b), 40);
    const titleClass = f.title.slice(0, sepIdx).toLowerCase().trim() || f.title.slice(0, 40).toLowerCase().trim();
    const key = `${f.severity}::${titleClass}`;
    const existing = map.get(key);
    if (existing) {
      existing.recurrence++;
      if (!existing.domains.includes(f.domain)) existing.domains.push(f.domain);
      if (!existing.statuses.includes(f.status)) existing.statuses.push(f.status);
    } else {
      map.set(key, {
        severity: f.severity,
        title: f.title,
        domains: [f.domain],
        recurrence: 1,
        statuses: [f.status],
      });
    }
  }
  return [...map.values()].sort((a, b) => b.recurrence - a.recurrence);
}

/** Count unique findings per severity level. */
export function uniqueSeverityCounts(
  deduped: DeduplicatedFinding[],
): Record<string, number> {
  const counts: Record<string, number> = { P0: 0, P1: 0, P2: 0, P3: 0 };
  for (const f of deduped) {
    if (f.severity in counts) counts[f.severity]++;
  }
  return counts;
}

function FindingRow({ f }: { f: DeduplicatedFinding }) {
  const [expanded, setExpanded] = useState(false);
  const multiDomain = f.domains.length > 1;

  return (
    <div
      className="card"
      style={{ padding: "10px 14px", cursor: f.recurrence > 1 || multiDomain ? "pointer" : "default" }}
      onClick={() => {
        if (f.recurrence > 1 || multiDomain) setExpanded((e) => !e);
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span
          className={`chip ${severityChipClass(f.severity)}`}
          style={{ flexShrink: 0, marginTop: 1 }}
        >
          {f.severity}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {f.title}
          </div>
          <div
            style={{
              fontSize: 10,
              color: "var(--text-dim)",
              marginTop: 2,
              fontFamily: "var(--font-mono)",
            }}
          >
            {f.domains[0]}{multiDomain ? ` +${f.domains.length - 1} more` : ""}
          </div>
        </div>
        {f.recurrence > 1 && (
          <span
            style={{
              flexShrink: 0,
              background: "var(--warn-soft)",
              color: "var(--warn)",
              border: "1px solid var(--warn)",
              borderRadius: 10,
              fontSize: 10,
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              padding: "2px 7px",
              marginTop: 1,
            }}
          >
            ×{f.recurrence}
          </span>
        )}
        {(f.recurrence > 1 || multiDomain) && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 10,
              color: "var(--text-dim)",
              marginTop: 3,
            }}
          >
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </div>
      {expanded && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid var(--border-soft)",
            fontSize: 10,
            fontFamily: "var(--font-mono)",
            color: "var(--text-dim)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div>
            <span style={{ color: "var(--text)" }}>Recurrences:</span> {f.recurrence}
          </div>
          {multiDomain && (
            <div>
              <span style={{ color: "var(--text)" }}>Domains:</span>{" "}
              {f.domains.join(", ")}
            </div>
          )}
          {f.statuses.length > 0 && (
            <div>
              <span style={{ color: "var(--text)" }}>Statuses:</span>{" "}
              {f.statuses.join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DiagnosticsScreen() {
  const [data, setData] = useState<DiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortByRecurrence, setSortByRecurrence] = useState(true);
  const [threadHealth, setThreadHealth] = useState<DigitalThreadHealthResponse | null>(null);
  const [showOrphans, setShowOrphans] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchDiagnostics();
      setData(res);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 5 * 60_000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const fetchThread = async () => {
      try {
        const res = await fetchDigitalThreadHealth();
        if (!cancelled) setThreadHealth(res);
      } catch { /* silently fail */ }
    };
    void fetchThread();
    const interval = setInterval(() => { void fetchThread(); }, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (loading) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "var(--text-dim)",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
        }}
      >
        Loading...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="quiet-state">
        <div className="glyph">◈</div>
        <h3>Diagnostics unavailable</h3>
        <p>Could not load diagnostic data.</p>
      </div>
    );
  }

  const { latest_run } = data;
  const findings = data.findings ?? [];
  const probe_coverage = data.probe_coverage ?? { total_probes: 0, probes_passing: 0, coverage_pct: 0 };
  const rawTrend = data.trend ?? [];
  // Bucket trend by calendar date, keeping the worst verdict per day (multiple runs/day → one bar).
  const VERDICT_ORDER: Record<string, number> = { RED: 0, YELLOW: 1, GREEN: 2 };
  const trendByDate = new Map<string, (typeof rawTrend)[0]>();
  for (const t of rawTrend) {
    const existing = trendByDate.get(t.date);
    if (!existing || (VERDICT_ORDER[t.verdict] ?? 3) < (VERDICT_ORDER[existing.verdict] ?? 3)) {
      trendByDate.set(t.date, t);
    }
  }
  const trend = [...trendByDate.values()];
  const verdict = latest_run?.overall_verdict ?? "RED";

  const deduped = deduplicateFindings(findings);
  const uniqueCounts = uniqueSeverityCounts(deduped);
  const sorted = sortByRecurrence
    ? deduped
    : [...deduped].sort((a, b) => a.severity.localeCompare(b.severity));

  // Scheduler runs every 6h — data older than 8h means a missed cycle (VLR-1150 issue 6).
  const isStale = latest_run != null && (Date.now() - new Date(latest_run.ran_at).getTime() > 28_800_000);
  const delta = trend.length >= 2 ? trend[trend.length - 1].finding_count - trend[trend.length - 2].finding_count : null;

  return (
    <>
      {/* Diagnostics hero */}
  <div style={{ padding: "16px 20px 0" }}>
    {isStale && latest_run && (
      <div style={{ background: "var(--warn-soft)", border: "1px solid var(--warn)", borderRadius: 8, padding: "6px 12px", marginBottom: 10, fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--warn)", display: "flex", alignItems: "center", gap: 6 }}>
        <span>⚠</span>
        <span>Stale — last run {timeAgo(latest_run.ran_at)}. Scheduler runs every 6h — a missed run is likely.</span>
      </div>
    )}
    <div className="card" style={{ padding: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: latest_run ? 12 : 0 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: VERDICT_BG[verdict],
            color: VERDICT_COLOR[verdict],
            border: "1px solid " + VERDICT_COLOR[verdict],
            borderRadius: 8,
            padding: "5px 16px",
            fontSize: 16,
            fontFamily: "var(--font-mono)",
            fontWeight: 700,
            letterSpacing: 3,
          }}
        >
          {latest_run ? verdict : "NO DATA"}
        </div>
        {latest_run ? (
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text)", fontWeight: 700 }}>
              {timeAgo(latest_run.ran_at)}
            </div>
            <div style={{ fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--text-dim)", marginTop: 1 }}>
              {new Date(latest_run.ran_at).toLocaleString()}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>No completed runs</div>
        )}
      </div>
      {latest_run && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(["P0", "P1", "P2", "P3"] as const).map((sev) => (
            <div key={sev} style={{ textAlign: "center", background: "var(--bg-elev-2)", borderRadius: 6, padding: "6px 10px", minWidth: 44 }}>
              <div style={{ fontSize: 14, fontFamily: "var(--font-mono)", fontWeight: 700, color: sev === "P0" || sev === "P1" ? "var(--danger)" : sev === "P2" ? "var(--warn)" : "var(--text)" }}>{uniqueCounts[sev]}</div>
              <div style={{ fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--text-dim)", marginTop: 2 }}>{sev}</div>
            </div>
          ))}
          {delta !== null && (
            <div style={{ textAlign: "center", background: "var(--bg-elev-2)", borderRadius: 6, padding: "6px 10px", minWidth: 44 }}>
              <div style={{ fontSize: 14, fontFamily: "var(--font-mono)", fontWeight: 700, color: delta > 0 ? "var(--danger)" : delta < 0 ? "var(--ok)" : "var(--text-dim)" }}>{delta > 0 ? "+" + String(delta) : String(delta)}</div>
              <div style={{ fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--text-dim)", marginTop: 2 }}>vs prev</div>
            </div>
          )}
        </div>
      )}
    </div>
  </div>

      {/* Probe coverage */}
      <div className="section-head">
        <h2>Probe Coverage</h2>
      </div>
      <div className="card" style={{ padding: "12px 14px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 8,
          }}
        >
          <span
            style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text)" }}
          >
            {probe_coverage.probes_passing} / {probe_coverage.total_probes} passing
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              fontWeight: 700,
              color:
                probe_coverage.coverage_pct >= 90
                  ? "var(--ok)"
                  : probe_coverage.coverage_pct >= 70
                    ? "var(--warn)"
                    : "var(--danger)",
            }}
          >
            {probe_coverage.coverage_pct}%
          </span>
        </div>
        <div
          style={{
            background: "var(--bg-elev-2)",
            borderRadius: 4,
            height: 6,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background:
                probe_coverage.coverage_pct >= 90
                  ? "var(--ok)"
                  : probe_coverage.coverage_pct >= 70
                    ? "var(--warn)"
                    : "var(--danger)",
              width: `${Math.min(probe_coverage.coverage_pct, 100)}%`,
              height: "100%",
              borderRadius: 4,
            }}
          />
        </div>
      </div>

      {/* Digital Thread */}
      <div className="section-head">
        <h2>Digital Thread</h2>
      </div>
      {threadHealth && (
        <div className="card" style={{ padding: "12px 14px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text)" }}>
              {threadHealth.annotatedFiles} / {threadHealth.totalFiles} files annotated
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                fontWeight: 700,
                color: annotationSeverityColor(threadHealth.annotationCoverage),
              }}
            >
              {threadHealth.annotationCoverage}%
            </span>
          </div>
          <div
            style={{
              background: "var(--bg-elev-2)",
              borderRadius: 4,
              height: 6,
              overflow: "hidden",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                background: annotationSeverityColor(threadHealth.annotationCoverage),
                width: `${Math.min(threadHealth.annotationCoverage, 100)}%`,
                height: "100%",
                borderRadius: 4,
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text)" }}>
              orphan modules (stale registry rows)
            </span>
            <button
              onClick={() => setShowOrphans((v) => !v)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: threadHealth.orphanCount > 0 ? "pointer" : "default",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                fontWeight: 700,
                color: orphanSeverityColor(threadHealth.orphanCount),
              }}
              aria-label="Toggle orphan module list"
            >
              {threadHealth.orphanCount}{threadHealth.orphanCount > 0 ? (showOrphans ? " ▲" : " ▼") : ""}
            </button>
          </div>
          {showOrphans && threadHealth.orphanModules && threadHealth.orphanModules.length > 0 && (
            <div
              style={{
                marginTop: 8,
                padding: "6px 8px",
                background: "var(--bg-elev-2)",
                borderRadius: 4,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {threadHealth.orphanModules.map((id) => (
                <div
                  key={id}
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--text-dim)",
                    padding: "2px 0",
                    wordBreak: "break-all",
                  }}
                >
                  {id}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Open findings */}
      <div className="section-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h2>Open Findings</h2>
          <span className="count">{deduped.length}</span>
          {findings.length !== deduped.length && (
            <span style={{ fontSize: 10, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
              ({findings.length} total)
            </span>
          )}
        </div>
        {deduped.length > 1 && (
          <button
            onClick={() => setSortByRecurrence((s) => !s)}
            style={{
              background: "none",
              border: "1px solid var(--border-soft)",
              borderRadius: 6,
              padding: "3px 10px",
              fontSize: 10,
              fontFamily: "var(--font-mono)",
              color: "var(--text-dim)",
              cursor: "pointer",
              marginRight: 20,
            }}
          >
            {sortByRecurrence ? "sort: recurrence ↓" : "sort: severity"}
          </button>
        )}
      </div>
      {deduped.length === 0 ? (
        <div className="quiet-state">
          <div className="glyph">✓</div>
          <h3>All clear</h3>
          <p>No open findings.</p>
        </div>
      ) : (
        sorted.map((f, i) => <FindingRow key={i} f={f} />)
      )}

      {trend.length > 0 && (() => {
    const maxCount = Math.max(...trend.map((t) => t.finding_count), 1);
    const latest = trend[trend.length - 1];
    return (
      <>
        <div className="section-head">
          <h2>Trend · last {trend.length} days</h2>
        </div>
        <div className="card" style={{ padding: "12px 14px" }}>
          <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 48, marginBottom: 8 }}>
            {trend.map((t, i) => {
              const h = Math.max(4, Math.round((t.finding_count / maxCount) * 48));
              const col = VERDICT_COLOR[t.verdict] ?? "var(--text-dim)";
              return (
                <div
                  key={i}
                  title={t.date + ": " + String(t.finding_count) + " (" + t.verdict + ")"}
                  style={{ flex: 1, height: h, background: col, borderRadius: "2px 2px 0 0", opacity: i === trend.length - 1 ? 1 : 0.6 }}
                />
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--text-dim)", marginBottom: 10 }}>
            <span>{trend[0]?.date}</span>
            <span>{latest.date}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8, borderTop: "1px solid var(--border-soft)" }}>
            <span className={"chip " + verdictChipClass(latest.verdict)} style={{ fontSize: 10 }}>
              {latest.verdict}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-dim)" }}>
              {latest.finding_count} finding{latest.finding_count !== 1 ? "s" : ""} · {latest.date}
            </span>
          </div>
        </div>
      </>
    );
  })()}

      <div style={{ height: 80 }} />
    </>
  );
}
